/* eslint-disable */
import { useState, useEffect } from "react";
import { saveToStorage, loadFromStorage, getSupabaseClient } from "./Auth";

/* ═══════════════════════════════════════════════════════════
   FOOTY ANALYST — MANAGER PROFILE
   Personal details · Club info · Coaching preferences
   Season stats · Account settings · Plan info
═══════════════════════════════════════════════════════════ */

const PROFILE_CSS = `
.prof-wrap { font-family:'DM Sans',sans-serif; background:var(--cream); min-height:100%; }

/* ── HERO ── */
.prof-hero {
  background:linear-gradient(135deg,var(--ink) 0%,var(--teal2) 100%);
  border-bottom:3px solid var(--mustard);
  padding:2rem 1.8rem 0;
  position:relative; overflow:hidden;
}
.prof-hero::before {
  content:'';
  position:absolute; inset:0;
  background:repeating-linear-gradient(0deg,transparent,transparent 40px,rgba(255,255,255,0.02) 40px,rgba(255,255,255,0.02) 41px);
  pointer-events:none;
}
.prof-hero-inner { position:relative; z-index:1; display:flex; align-items:flex-end; gap:1.5rem; flex-wrap:wrap; }

/* Avatar */
.prof-avatar-wrap { position:relative; flex-shrink:0; }
.prof-avatar {
  width:88px; height:88px;
  background:var(--teal); border:3px solid var(--mustard);
  display:flex; align-items:center; justify-content:center;
  font-family:'Bebas Neue',sans-serif; font-size:2rem; letter-spacing:3px;
  color:var(--white); position:relative;
}
.prof-avatar-badge {
  position:absolute; bottom:-6px; right:-6px;
  width:26px; height:26px;
  background:var(--mustard); border:2px solid var(--ink);
  display:flex; align-items:center; justify-content:center;
  font-size:0.75rem;
}
.prof-avatar-edit {
  position:absolute; inset:0;
  background:rgba(0,0,0,0.4); opacity:0;
  display:flex; align-items:center; justify-content:center;
  cursor:pointer; transition:opacity 0.2s;
  font-size:0.65rem; font-weight:800; letter-spacing:1px;
  text-transform:uppercase; color:white;
}
.prof-avatar-wrap:hover .prof-avatar-edit { opacity:1; }

/* Hero info */
.prof-hero-info { flex:1; padding-bottom:1.2rem; }
.prof-hero-name { font-family:'Bebas Neue',sans-serif; font-size:2rem; letter-spacing:3px; color:var(--white); line-height:1; }
.prof-hero-club { font-size:0.82rem; color:rgba(255,255,255,0.55); font-weight:300; margin-top:0.25rem; }
.prof-hero-tags { display:flex; gap:0.4rem; margin-top:0.6rem; flex-wrap:wrap; }
.prof-hero-tag { font-size:0.58rem; font-weight:800; padding:0.14rem 0.5rem; letter-spacing:2px; text-transform:uppercase; }
.prof-hero-tag.role { background:var(--teal); color:var(--white); }
.prof-hero-tag.plan { background:var(--mustard); color:var(--ink); }
.prof-hero-tag.badge { background:rgba(255,255,255,0.12); color:rgba(255,255,255,0.7); border:1px solid rgba(255,255,255,0.2); }

/* Hero tabs */
.prof-hero-tabs { display:flex; margin-top:1rem; }
.prof-hero-tab {
  padding:0.72rem 1.1rem; font-family:'DM Sans',sans-serif; font-size:0.7rem;
  font-weight:800; letter-spacing:1.5px; text-transform:uppercase;
  cursor:pointer; color:rgba(255,255,255,0.45);
  border-bottom:3px solid transparent; margin-bottom:-3px;
  transition:all 0.18s; white-space:nowrap;
}
.prof-hero-tab:hover { color:rgba(255,255,255,0.75); }
.prof-hero-tab.on { color:var(--mustard); border-bottom-color:var(--mustard); }

/* ── CONTENT ── */
.prof-content { padding:1.5rem 1.8rem; max-width:820px; }

/* Panels */
.prof-panel { background:var(--white); border:1.5px solid var(--rule); margin-bottom:1.2rem; }
.prof-panel-head {
  padding:0.85rem 1.1rem; border-bottom:1.5px solid var(--rule);
  display:flex; align-items:center; justify-content:space-between;
}
.prof-panel-title { font-family:'Bebas Neue',sans-serif; font-size:0.9rem; letter-spacing:2.5px; color:var(--ink); }
.prof-panel-body { padding:1.1rem; }

/* Form elements */
.prof-form-grid { display:grid; grid-template-columns:1fr 1fr; gap:0.85rem; }
.prof-form-group { display:flex; flex-direction:column; }
.prof-label { font-size:0.58rem; font-weight:800; letter-spacing:2px; text-transform:uppercase; color:var(--muted); margin-bottom:0.35rem; }
.prof-input {
  background:var(--cream); border:1.5px solid var(--rule);
  border-bottom:2px solid var(--ink); padding:0.6rem 0.85rem;
  color:var(--ink); font-family:'DM Sans',sans-serif; font-size:0.85rem;
  font-weight:500; outline:none; transition:border-color 0.2s;
}
.prof-input:focus { border-bottom-color:var(--teal); }
.prof-input:disabled { opacity:0.5; cursor:not-allowed; background:var(--cream2); }
.prof-select {
  background:var(--cream); border:1.5px solid var(--rule);
  border-bottom:2px solid var(--ink); padding:0.6rem 0.85rem;
  color:var(--ink); font-family:'DM Sans',sans-serif; font-size:0.85rem;
  font-weight:500; outline:none; cursor:pointer; transition:border-color 0.2s;
}
.prof-select:focus { border-bottom-color:var(--teal); }

/* Stats grid */
.prof-stats { display:grid; grid-template-columns:repeat(4,1fr); gap:0; }
.prof-stat { padding:0.9rem 1rem; text-align:center; border-right:1.5px solid var(--rule); }
.prof-stat:last-child { border-right:none; }
.prof-stat-val { font-family:'Bebas Neue',sans-serif; font-size:1.8rem; letter-spacing:2px; color:var(--ink); line-height:1; }
.prof-stat-val.teal { color:var(--teal); }
.prof-stat-val.green { color:#16a34a; }
.prof-stat-val.red { color:var(--darkred); }
.prof-stat-val.mustard { color:var(--mustard2); }
.prof-stat-lbl { font-size:0.58rem; font-weight:700; letter-spacing:2px; text-transform:uppercase; color:var(--muted); margin-top:0.15rem; }

/* Coaching badge selector */
.prof-badge-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:0.5rem; }
.prof-badge-opt {
  padding:0.65rem 0.5rem; border:1.5px solid var(--rule);
  cursor:pointer; text-align:center; transition:all 0.18s;
}
.prof-badge-opt:hover { border-color:var(--teal); }
.prof-badge-opt.on { border-color:var(--mustard); background:var(--mustard3); }
.prof-badge-icon { font-size:1.4rem; margin-bottom:0.3rem; }
.prof-badge-name { font-size:0.68rem; font-weight:800; color:var(--ink); }
.prof-badge-sub { font-size:0.58rem; color:var(--muted); font-weight:300; margin-top:0.1rem; }

/* Colour picker for kit */
.prof-colour-row { display:flex; gap:0.5rem; flex-wrap:wrap; }
.prof-colour { width:32px; height:32px; border-radius:50%; cursor:pointer; border:3px solid transparent; transition:all 0.18s; }
.prof-colour.on { border-color:var(--ink); transform:scale(1.15); }

/* Formation preference */
.prof-form-chips { display:flex; gap:0.4rem; flex-wrap:wrap; }
.prof-form-chip {
  padding:0.38rem 0.85rem; border:1.5px solid var(--rule);
  font-family:'DM Sans',sans-serif; font-size:0.72rem; font-weight:800;
  letter-spacing:1px; cursor:pointer; color:var(--muted); transition:all 0.18s;
}
.prof-form-chip:hover { border-color:var(--teal); color:var(--teal); }
.prof-form-chip.on { background:var(--teal); border-color:var(--teal); color:var(--white); }

/* Playing style */
.prof-style-grid { display:grid; grid-template-columns:1fr 1fr; gap:0.4rem; }
.prof-style-opt {
  padding:0.55rem 0.85rem; border:1.5px solid var(--rule);
  cursor:pointer; transition:all 0.18s;
  display:flex; align-items:center; gap:0.5rem;
}
.prof-style-opt:hover { border-color:var(--teal); background:var(--teal4); }
.prof-style-opt.on { border-color:var(--teal); background:var(--teal4); border-left:4px solid var(--teal); }
.prof-style-opt-icon { font-size:1rem; flex-shrink:0; }
.prof-style-opt-name { font-size:0.78rem; font-weight:800; color:var(--ink); }
.prof-style-opt-desc { font-size:0.62rem; color:var(--muted); font-weight:300; }

/* Save button */
.prof-save-btn {
  padding:0.78rem 2rem; background:var(--teal); color:var(--white);
  border:2px solid var(--teal); font-family:'DM Sans',sans-serif;
  font-weight:800; font-size:0.75rem; letter-spacing:2px;
  text-transform:uppercase; cursor:pointer; transition:all 0.2s;
}
.prof-save-btn:hover { background:var(--teal2); }
.prof-save-btn.saved { background:#16a34a; border-color:#16a34a; }
.prof-cancel-btn {
  padding:0.78rem 1.5rem; background:transparent; color:var(--muted);
  border:1.5px solid var(--rule); font-family:'DM Sans',sans-serif;
  font-weight:700; font-size:0.75rem; letter-spacing:2px;
  text-transform:uppercase; cursor:pointer;
}

/* Danger zone */
.prof-danger { background:var(--darkred3); border:1.5px solid var(--darkred); padding:1rem; display:flex; align-items:center; justify-content:space-between; gap:1rem; flex-wrap:wrap; }
.prof-danger-text { font-size:0.78rem; color:var(--darkred); font-weight:500; }
.prof-danger-btn { padding:0.5rem 1rem; background:var(--darkred); color:var(--white); border:none; font-family:'DM Sans',sans-serif; font-weight:800; font-size:0.68rem; letter-spacing:1.5px; text-transform:uppercase; cursor:pointer; }

/* Plan card */
.prof-plan-card { border:1.5px solid var(--rule); padding:1rem; display:flex; align-items:center; gap:1rem; }
.prof-plan-card.active { border-color:var(--mustard); background:var(--mustard3); }
.prof-plan-icon { font-size:1.8rem; flex-shrink:0; }
.prof-plan-name { font-family:'Bebas Neue',sans-serif; font-size:1rem; letter-spacing:2px; color:var(--ink); }
.prof-plan-price { font-size:0.68rem; color:var(--muted); font-weight:300; }
.prof-plan-features { font-size:0.68rem; color:var(--ink2); font-weight:400; margin-top:0.2rem; }
.prof-plan-badge { font-size:0.56rem; font-weight:800; padding:0.12rem 0.45rem; letter-spacing:2px; text-transform:uppercase; background:var(--mustard); color:var(--ink); margin-left:auto; flex-shrink:0; }

/* Activity feed */
.prof-activity-row { display:flex; align-items:flex-start; gap:0.65rem; padding:0.55rem 0; border-bottom:1px solid var(--rule2); }
.prof-activity-row:last-child { border-bottom:none; }
.prof-activity-icon { width:28px; height:28px; border-radius:50%; background:var(--teal4); border:1.5px solid var(--teal3); display:flex; align-items:center; justify-content:center; font-size:0.75rem; flex-shrink:0; }
.prof-activity-text { font-size:0.78rem; color:var(--ink2); font-weight:400; flex:1; line-height:1.45; }
.prof-activity-time { font-size:0.62rem; color:var(--muted); font-style:italic; flex-shrink:0; }

@media(max-width:700px) {
  .prof-form-grid { grid-template-columns:1fr; }
  .prof-stats { grid-template-columns:1fr 1fr; }
  .prof-badge-grid { grid-template-columns:1fr 1fr; }
  .prof-style-grid { grid-template-columns:1fr; }
  .prof-hero { padding:1.2rem 1rem 0; }
  .prof-hero-name { font-size:1.5rem; }
  .prof-hero-tabs { overflow-x:auto; }
  .prof-content { padding:0.85rem; }
  .prof-colour-row { gap:0.4rem; }
  .prof-colour { width:28px; height:28px; }
  .prof-plan-card { flex-wrap:wrap; }
}
`;

const COACHING_BADGES = [
  { id:"none",    icon:"📋", name:"No Badge",    sub:"Just getting started" },
  { id:"fa-l1",  icon:"🎖️", name:"FA Level 1",  sub:"Introduction to coaching" },
  { id:"fa-l2",  icon:"🏅", name:"FA Level 2",  sub:"Foundation licence" },
  { id:"uefa-b", icon:"🥉", name:"UEFA B",       sub:"Youth & adult coaching" },
  { id:"uefa-a", icon:"🥈", name:"UEFA A",       sub:"Semi-pro level" },
  { id:"pro",    icon:"🥇", name:"UEFA Pro",     sub:"Elite qualification" },
];

const PLAYING_STYLES = [
  { id:"possession", icon:"🔵", name:"Possession Play", desc:"Control through the ball" },
  { id:"press",      icon:"⚡", name:"High Press",      desc:"Win it back immediately" },
  { id:"counter",    icon:"🚀", name:"Counter-attack",  desc:"Fast vertical transitions" },
  { id:"lowblock",   icon:"🛡️", name:"Low Block",       desc:"Defensive and compact" },
  { id:"direct",     icon:"📏", name:"Direct Football", desc:"Simple and effective" },
  { id:"hybrid",     icon:"🔄", name:"Hybrid/Flexible", desc:"Adapt to the opponent" },
];

const KIT_COLOURS = [
  "#2e7d7a","#8b2a2a","#1e3a5f","#16a34a","#d4941a","#6d28d9",
  "#db2777","#1a1612","#374151","#b45309",
];

const FORMATIONS = ["4-3-3","4-4-2","4-2-3-1","3-5-2","3-4-3","4-1-4-1","5-3-2","4-3-2-1"];

function ini(name) {
  return (name||"?").split(" ").map(w=>w[0]).join("").toUpperCase().slice(0,2);
}

/* ── LOAD PROFILE FROM STORAGE ── */
function loadProfile(user) {
  const stored = loadFromStorage(`profile_${user?.id}`, {});
  return {
    name:        stored.name        || user?.name  || "",
    email:       stored.email       || user?.email || "",
    club:        stored.club        || loadFromStorage("fx_club_name","") || "",
    role:        stored.role        || user?.role  || "coach",
    badge:       stored.badge       || "none",
    style:       stored.style       || "possession",
    formation:   stored.formation   || "4-3-3",
    kitColour:   stored.kitColour   || "#2e7d7a",
    bio:         stored.bio         || "",
    yearsCoaching: stored.yearsCoaching || "",
    website:     stored.website     || "",
    twitter:     stored.twitter     || "",
  };
}

/* ══════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════ */
export default function Profile({ user, onUpdate, onSignOut }) {
  const [activeTab, setActiveTab] = useState("profile");
  const [form, setForm] = useState(() => loadProfile(user));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [avatarColour, setAvatarColour] = useState(
    loadFromStorage(`profile_${user?.id}`, {})?.kitColour || "#2e7d7a"
  );

  // Derived stats from saved data
  const squadSize   = (loadFromStorage("my_squad", []) || []).length;
  const reports     = (loadFromStorage("scout_reports", []) || []).length;
  const fixtures    = (loadFromStorage("fx_fixtures", []) || []);
  const played      = fixtures.filter(f => f.played);
  const wins        = played.filter(f => f.goalsFor > f.goalsAgainst).length;
  const drills      = (loadFromStorage("session_drills", []) || []).length;
  const watchlist   = (loadFromStorage("watchlist_ids", []) || []).length;
  const winRate     = played.length > 0 ? Math.round((wins/played.length)*100) : 0;

  // Recent activity from stored data
  const activity = [
    squadSize > 0  && { icon:"🏟️", text:`Squad has ${squadSize} players registered`, time:"Today" },
    reports > 0    && { icon:"📋", text:`${reports} scout report${reports!==1?"s":""} filed this season`, time:"This week" },
    played.length > 0 && { icon:"⚽", text:`Last result: ${played[played.length-1]?.opponent||"Unknown"} ${played[played.length-1]?.goalsFor}–${played[played.length-1]?.goalsAgainst}`, time:"Recent" },
    drills > 0     && { icon:"📅", text:`${drills} training drill${drills!==1?"s":""} saved to session planner`, time:"Recent" },
  ].filter(Boolean);

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const saveProfile = async () => {
    setSaving(true);
    const profileData = { ...form, kitColour: avatarColour };

    // Save to localStorage
    saveToStorage(`profile_${user?.id}`, profileData);
    // Clear the pending onboarding profile now that we have the real one
    try { localStorage.removeItem('profile_pending'); } catch(e) {}
    saveToStorage("fx_club_name", form.club);

    // Save to Supabase profiles table if connected
    try {
      const sb = getSupabaseClient();
      if (sb && user?.id && !user.id.startsWith("demo_") && !user.id.startsWith("local_")) {
        await sb.from("profiles").upsert({
          id: user.id,
          name: form.name,
          email: form.email,
          role: form.role,
          club: form.club,
          updated_at: new Date().toISOString(),
        });
      }
    } catch(e) {
      console.warn("Profile cloud save failed:", e.message);
    }

    // Notify parent to update user context
    if (onUpdate) {
      onUpdate({
        ...user,
        name: form.name,
        club: form.club,
        initials: ini(form.name),
      });
    }

    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const TABS = [
    { id:"profile",  label:"Profile" },
    { id:"prefs",    label:"Preferences" },
    { id:"stats",    label:"My Season" },
    { id:"account",  label:"Account" },
  ];

  return (
    <>
      <style>{PROFILE_CSS}</style>
      <div className="prof-wrap">

        {/* HERO */}
        <div className="prof-hero">
          <div className="prof-hero-inner">
            {/* Avatar */}
            <div className="prof-avatar-wrap">
              <div className="prof-avatar" style={{background:avatarColour}}>
                {ini(form.name||user?.name||"FA")}
                <div className="prof-avatar-edit">Edit</div>
              </div>
              <div className="prof-avatar-badge">
                {COACHING_BADGES.find(b=>b.id===form.badge)?.icon||"📋"}
              </div>
            </div>

            {/* Info */}
            <div className="prof-hero-info">
              <div className="prof-hero-name">{form.name||user?.name||"Your Name"}</div>
              <div className="prof-hero-club">
                {form.club||"No club set"} · {form.yearsCoaching?`${form.yearsCoaching} years coaching · `:""}
                {COACHING_BADGES.find(b=>b.id===form.badge)?.name||"No Badge"}
              </div>
              <div className="prof-hero-tags">
                <span className="prof-hero-tag role">{form.role==="scout"?"🔍 Scout":"🎯 Coach"}</span>
                <span className="prof-hero-tag plan">{user?.plan||"Pro"} Plan</span>
                <span className="prof-hero-tag badge">
                  {PLAYING_STYLES.find(s=>s.id===form.style)?.icon} {PLAYING_STYLES.find(s=>s.id===form.style)?.name||""}
                </span>
                <span className="prof-hero-tag badge">⚽ {form.formation}</span>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="prof-hero-tabs">
            {TABS.map(t=>(
              <div key={t.id} className={`prof-hero-tab ${activeTab===t.id?"on":""}`} onClick={()=>setActiveTab(t.id)}>
                {t.label}
              </div>
            ))}
          </div>
        </div>

        <div className="prof-content">

          {/* ══ PROFILE TAB ══ */}
          {activeTab==="profile"&&(
            <>
              {/* Personal details */}
              <div className="prof-panel">
                <div className="prof-panel-head">
                  <div className="prof-panel-title">Personal Details</div>
                </div>
                <div className="prof-panel-body">
                  <div className="prof-form-grid">
                    <div className="prof-form-group">
                      <label className="prof-label">Full Name *</label>
                      <input className="prof-input" placeholder="e.g. James Clarke" value={form.name} onChange={e=>set("name",e.target.value)}/>
                    </div>
                    <div className="prof-form-group">
                      <label className="prof-label">Email</label>
                      <input className="prof-input" value={form.email||user?.email||""} disabled/>
                    </div>
                    <div className="prof-form-group">
                      <label className="prof-label">Role</label>
                      <select className="prof-select" value={form.role} onChange={e=>set("role",e.target.value)}>
                        <option value="coach">Head Coach</option>
                        <option value="assistant">Assistant Coach</option>
                        <option value="scout">Scout</option>
                        <option value="analyst">Analyst</option>
                        <option value="physio">Physio / Sports Science</option>
                      </select>
                    </div>
                    <div className="prof-form-group">
                      <label className="prof-label">Years Coaching</label>
                      <input className="prof-input" type="number" min="0" max="50" placeholder="e.g. 8" value={form.yearsCoaching} onChange={e=>set("yearsCoaching",e.target.value)}/>
                    </div>
                  </div>
                  <div style={{marginTop:"0.85rem"}}>
                    <label className="prof-label">Bio</label>
                    <textarea className="prof-input" style={{resize:"vertical",minHeight:80,fontStyle:"italic",fontWeight:300}}
                      placeholder="A short description of your coaching philosophy and experience..."
                      value={form.bio} onChange={e=>set("bio",e.target.value)}/>
                  </div>
                </div>
              </div>

              {/* Club details */}
              <div className="prof-panel">
                <div className="prof-panel-head">
                  <div className="prof-panel-title">Club Details</div>
                </div>
                <div className="prof-panel-body">
                  <div className="prof-form-grid">
                    <div className="prof-form-group">
                      <label className="prof-label">Club Name *</label>
                      <input className="prof-input" placeholder="e.g. FC Rovers" value={form.club} onChange={e=>set("club",e.target.value)}/>
                    </div>
                    <div className="prof-form-group">
                      <label className="prof-label">Website</label>
                      <input className="prof-input" placeholder="e.g. fcroverssports.com" value={form.website} onChange={e=>set("website",e.target.value)}/>
                    </div>
                  </div>

                  <div style={{marginTop:"0.85rem"}}>
                    <label className="prof-label">Club Colour</label>
                    <div className="prof-colour-row">
                      {KIT_COLOURS.map(c=>(
                        <div key={c} className={`prof-colour ${avatarColour===c?"on":""}`}
                          style={{background:c}}
                          onClick={()=>setAvatarColour(c)}/>
                      ))}
                    </div>
                    <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.62rem",color:"var(--muted)",fontStyle:"italic",marginTop:"0.4rem"}}>
                      This colour is used for your profile avatar
                    </div>
                  </div>
                </div>
              </div>

              {/* Coaching badge */}
              <div className="prof-panel">
                <div className="prof-panel-head">
                  <div className="prof-panel-title">Coaching Qualification</div>
                </div>
                <div className="prof-panel-body">
                  <div className="prof-badge-grid">
                    {COACHING_BADGES.map(b=>(
                      <div key={b.id} className={`prof-badge-opt ${form.badge===b.id?"on":""}`} onClick={()=>set("badge",b.id)}>
                        <div className="prof-badge-icon">{b.icon}</div>
                        <div className="prof-badge-name">{b.name}</div>
                        <div className="prof-badge-sub">{b.sub}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Save */}
              <div style={{display:"flex",gap:"0.75rem"}}>
                <button className={`prof-save-btn ${saved?"saved":""}`} onClick={saveProfile} disabled={saving}>
                  {saving?"Saving...":saved?"✓ Saved!":"Save Profile"}
                </button>
              </div>
            </>
          )}

          {/* ══ PREFERENCES TAB ══ */}
          {activeTab==="prefs"&&(
            <>
              {/* Preferred formation */}
              <div className="prof-panel">
                <div className="prof-panel-head">
                  <div className="prof-panel-title">Preferred Formation</div>
                </div>
                <div className="prof-panel-body">
                  <div className="prof-form-chips">
                    {FORMATIONS.map(f=>(
                      <div key={f} className={`prof-form-chip ${form.formation===f?"on":""}`} onClick={()=>set("formation",f)}>{f}</div>
                    ))}
                  </div>
                  <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.65rem",color:"var(--muted)",fontStyle:"italic",marginTop:"0.6rem"}}>
                    Used as the default in Squad Builder, Tactics Board and Session Builder
                  </div>
                </div>
              </div>

              {/* Playing style */}
              <div className="prof-panel">
                <div className="prof-panel-head">
                  <div className="prof-panel-title">Playing Style</div>
                </div>
                <div className="prof-panel-body">
                  <div className="prof-style-grid">
                    {PLAYING_STYLES.map(s=>(
                      <div key={s.id} className={`prof-style-opt ${form.style===s.id?"on":""}`} onClick={()=>set("style",s.id)}>
                        <div className="prof-style-opt-icon">{s.icon}</div>
                        <div>
                          <div className="prof-style-opt-name">{s.name}</div>
                          <div className="prof-style-opt-desc">{s.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.65rem",color:"var(--muted)",fontStyle:"italic",marginTop:"0.6rem"}}>
                    Used by the AI Analyst to personalise recommendations
                  </div>
                </div>
              </div>

              {/* Save */}
              <div style={{display:"flex",gap:"0.75rem"}}>
                <button className={`prof-save-btn ${saved?"saved":""}`} onClick={saveProfile} disabled={saving}>
                  {saving?"Saving...":saved?"✓ Saved!":"Save Preferences"}
                </button>
              </div>
            </>
          )}

          {/* ══ SEASON STATS TAB ══ */}
          {activeTab==="stats"&&(
            <>
              {/* Season stats */}
              <div className="prof-panel">
                <div className="prof-panel-head">
                  <div className="prof-panel-title">2024/25 Season Overview</div>
                </div>
                <div className="prof-stats">
                  <div className="prof-stat">
                    <div className="prof-stat-val teal">{played.length}</div>
                    <div className="prof-stat-lbl">Matches Played</div>
                  </div>
                  <div className="prof-stat">
                    <div className="prof-stat-val green">{wins}</div>
                    <div className="prof-stat-lbl">Wins</div>
                  </div>
                  <div className="prof-stat">
                    <div className="prof-stat-val mustard">{winRate}%</div>
                    <div className="prof-stat-lbl">Win Rate</div>
                  </div>
                  <div className="prof-stat">
                    <div className="prof-stat-val">{played.filter(f=>f.goalsFor>f.goalsAgainst).length*3+played.filter(f=>f.goalsFor===f.goalsAgainst).length}</div>
                    <div className="prof-stat-lbl">Points</div>
                  </div>
                </div>
              </div>

              {/* Platform usage */}
              <div className="prof-panel">
                <div className="prof-panel-head">
                  <div className="prof-panel-title">Platform Usage</div>
                </div>
                <div className="prof-stats">
                  <div className="prof-stat">
                    <div className="prof-stat-val teal">{squadSize}</div>
                    <div className="prof-stat-lbl">Squad Players</div>
                  </div>
                  <div className="prof-stat">
                    <div className="prof-stat-val mustard">{reports}</div>
                    <div className="prof-stat-lbl">Scout Reports</div>
                  </div>
                  <div className="prof-stat">
                    <div className="prof-stat-val">{drills}</div>
                    <div className="prof-stat-lbl">Saved Drills</div>
                  </div>
                  <div className="prof-stat">
                    <div className="prof-stat-val green">{fixtures.length}</div>
                    <div className="prof-stat-lbl">Fixtures</div>
                  </div>
                </div>
              </div>

              {/* Recent activity */}
              <div className="prof-panel">
                <div className="prof-panel-head">
                  <div className="prof-panel-title">Recent Activity</div>
                </div>
                <div className="prof-panel-body">
                  {activity.length === 0 ? (
                    <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.78rem",color:"var(--muted)",fontStyle:"italic",textAlign:"center",padding:"1.5rem"}}>
                      No activity yet — start building your squad and filing reports
                    </div>
                  ) : activity.map((a,i)=>(
                    <div key={i} className="prof-activity-row">
                      <div className="prof-activity-icon">{a.icon}</div>
                      <div className="prof-activity-text">{a.text}</div>
                      <div className="prof-activity-time">{a.time}</div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* ══ ACCOUNT TAB ══ */}
          {activeTab==="account"&&(
            <>
              {/* Current plan */}
              <div className="prof-panel">
                <div className="prof-panel-head">
                  <div className="prof-panel-title">Subscription Plan</div>
                </div>
                <div className="prof-panel-body" style={{display:"flex",flexDirection:"column",gap:"0.55rem"}}>
                  {[
                    {id:"basic",  icon:"📋", name:"Basic",  price:"£19/month", features:"Video analysis · 2 pro styles · Player database", active:user?.plan==="basic"},
                    {id:"pro",    icon:"⚽", name:"Pro",    price:"£49/month", features:"Everything in Basic · All AI features · Full drills library · Scout reports", active:user?.plan==="pro"||!user?.plan},
                    {id:"elite",  icon:"🏆", name:"Elite",  price:"£99/month", features:"Everything in Pro · Unlimited AI · Multi-team · Priority support", active:user?.plan==="elite"},
                  ].map(plan=>(
                    <div key={plan.id} className={`prof-plan-card ${plan.active?"active":""}`}>
                      <div className="prof-plan-icon">{plan.icon}</div>
                      <div style={{flex:1}}>
                        <div className="prof-plan-name">{plan.name}</div>
                        <div className="prof-plan-price">{plan.price}</div>
                        <div className="prof-plan-features">{plan.features}</div>
                      </div>
                      {plan.active
                        ? <span className="prof-plan-badge">Current Plan</span>
                        : <button style={{padding:"0.38rem 0.85rem",background:"var(--teal)",color:"var(--white)",border:"none",fontFamily:"'DM Sans',sans-serif",fontWeight:800,fontSize:"0.62rem",letterSpacing:"1.5px",textTransform:"uppercase",cursor:"pointer"}}>Upgrade</button>
                      }
                    </div>
                  ))}
                </div>
              </div>

              {/* Account info */}
              <div className="prof-panel">
                <div className="prof-panel-head">
                  <div className="prof-panel-title">Account Information</div>
                </div>
                <div className="prof-panel-body">
                  <div className="prof-form-grid">
                    <div className="prof-form-group">
                      <label className="prof-label">Email Address</label>
                      <input className="prof-input" value={user?.email||""} disabled/>
                    </div>
                    <div className="prof-form-group">
                      <label className="prof-label">Account ID</label>
                      <input className="prof-input" value={user?.id?.slice(0,16)+"..." ||"—"} disabled/>
                    </div>
                  </div>
                  <div style={{marginTop:"0.85rem"}}>
                    <button style={{padding:"0.55rem 1.2rem",background:"transparent",color:"var(--teal)",border:"1.5px solid var(--teal)",fontFamily:"'DM Sans',sans-serif",fontWeight:800,fontSize:"0.68rem",letterSpacing:"1.5px",textTransform:"uppercase",cursor:"pointer"}}>
                      Change Password
                    </button>
                  </div>
                </div>
              </div>

              {/* Danger zone */}
              <div className="prof-panel">
                <div className="prof-panel-head">
                  <div className="prof-panel-title" style={{color:"var(--darkred)"}}>Danger Zone</div>
                </div>
                <div className="prof-panel-body" style={{display:"flex",flexDirection:"column",gap:"0.6rem"}}>
                  <div className="prof-danger">
                    <div>
                      <div className="prof-danger-text" style={{fontWeight:700,marginBottom:"0.2rem"}}>Sign out of all devices</div>
                      <div className="prof-danger-text" style={{fontWeight:300}}>Clears your session on this and all other devices</div>
                    </div>
                    <button className="prof-danger-btn" onClick={onSignOut}>Sign Out</button>
                  </div>
                  <div className="prof-danger">
                    <div>
                      <div className="prof-danger-text" style={{fontWeight:700,marginBottom:"0.2rem"}}>Clear local data</div>
                      <div className="prof-danger-text" style={{fontWeight:300}}>Removes cached data from this browser. Cloud data is preserved.</div>
                    </div>
                    <button className="prof-danger-btn" onClick={()=>{
                      if(window.confirm("Clear all local cached data? Your cloud data will not be affected.")) {
                        const keysToKeep = ["session"];
                        Object.keys(localStorage).filter(k=>k.startsWith("footyanalyst_")&&!keysToKeep.includes(k.replace("footyanalyst_",""))).forEach(k=>localStorage.removeItem(k));
                        window.location.reload();
                      }
                    }}>Clear Cache</button>
                  </div>
                </div>
              </div>
            </>
          )}

        </div>
      </div>
    </>
  );
}
